/**
 * /ask — AI-search-optimized FAQ knowledge base.
 *
 * Answers 25 canonical questions that US buyers, retailers, brands and
 * hospitality operators most often ask about sourcing from India.
 *
 * Design goal: single authoritative page (no thin sub-pages), organised
 * by topic, with a valid FAQPage JSON-LD graph whose Q/A pairs exactly
 * match the visible copy. Optimised for Google AI Overviews, ChatGPT
 * Search, Perplexity, Gemini and Copilot answer engines.
 *
 * Each answer is "answer-first" (a direct concise answer in the first
 * sentence) followed by useful context and, where relevant, a contextual
 * internal link into the site.
 */

export const ASK_TOPICS = [
  {
    id: "getting-started",
    label: "Getting started",
    intro:
      "The first-week questions a US buyer new to India sourcing needs answered before writing a brief or wiring any money.",
    questions: [
      {
        id: "how-do-i-find-a-sourcing-agent-in-india",
        q: "How do I find a sourcing agent in India?",
        a: "The safest path is to hire a buyer-side India sourcing agent — a firm whose fee comes from you (transparent commission on FOB or fixed retainer), never from the factory. Ask for a written engagement letter, a signed NDA, GST/PAN and MCA verification of the agent's own legal entity, at least two contactable buyer references, and a documented shortlisting workflow (e.g. legal-entity check → capacity walk → BSCI / Sedex verification → sample round). Trade fairs (IHGF Delhi, Home Textile Fair) and Alibaba are lead sources, not screening tools — verify every referral. See our operational playbook at /india-sourcing-agent-for-usa.",
        links: [
          { label: "India sourcing agent for USA", to: "/india-sourcing-agent-for-usa" },
        ],
      },
      {
        id: "sourcing-agent-vs-buying-agent",
        q: "What is the difference between an India sourcing agent and an India buying agent?",
        a: "A sourcing agent works upstream of the PO — category strategy, factory shortlisting, price benchmarking, sample development, supplier verification. A buying agent works from the PO onward — PO issuance, LC / TT payment flows, in-line and final AQL inspection, export documentation, container consolidation. Many US buyers engage the same firm for both. Read the full comparison at /trends/india-sourcing-agent-vs-buying-agent.",
        links: [
          { label: "Sourcing agent vs buying agent", to: "/trends/india-sourcing-agent-vs-buying-agent" },
          { label: "India buying agent for USA", to: "/india-buying-agent-for-usa" },
        ],
      },
      {
        id: "cost-of-india-sourcing-agent",
        q: "How much does an India sourcing agent cost?",
        a: "Two structures are typical. Percentage buying-agent commission on FOB: 5-10% depending on category, volume and complexity. Fixed monthly retainer for supply-chain-management engagements: negotiated per programme. The fee should always be buyer-side (paid by you), disclosed in writing before work starts, and never taken as a factory kickback. Any agent who says the factory pays them is a factory sales rep, not an agent.",
        links: [
          { label: "See fee model at /india-sourcing-agent-for-usa", to: "/india-sourcing-agent-for-usa" },
        ],
      },
      {
        id: "do-i-need-to-visit-india",
        q: "Do I need to visit India to source from India?",
        a: "Not for the first shortlist, but yes before signing a long-term programme. Our default US-buyer workflow is: (1) remote brief + shortlist + first-sample round with photos and video walkthroughs; (2) if the round passes, you fly in for a 5-day cluster visit (Delhi + relevant cluster: Moradabad, Jaipur, Jodhpur, Panipat, Bhadohi, or Kashmir) to walk the factory, meet the owner and lock the programme; (3) subsequent POs run remotely with in-line and final QC. Most US airlines fly direct to Delhi (Air India, United, Delta).",
        links: [],
      },
      {
        id: "how-long-does-sourcing-take",
        q: "How long does it take to source from India from brief to first container?",
        a: "For a first programme: 4-6 months end-to-end. Product development 3-5 weeks (first article), sample iteration 2-4 weeks, PO acceptance to production complete 6-10 weeks (category-dependent), freight to US ports 24-35 days. Repeat POs against an established SKU run 2-3 months end-to-end because the development and factory-selection steps collapse.",
        links: [
          { label: "Complete sourcing guide", to: "/trends/complete-guide-sourcing-products-india" },
        ],
      },
    ],
  },
  {
    id: "moq-and-pricing",
    label: "MOQ, pricing & tariffs",
    intro:
      "How Indian factory economics differ from China, what MOQs actually look like on the ground, and what US tariff exposure applies.",
    questions: [
      {
        id: "moq-india",
        q: "What is the typical MOQ in India?",
        a: "Category-dependent, but broadly: home décor 200-500 units per SKU; lighting 300 units per SKU; solid-wood furniture 100-200 units per SKU; home textile 500 units per SKU; hand-blown glass 1,000 units per SKU; blue pottery 300 units per SKU. Well below typical Chinese MOQs (3,000-5,000). This is why India fits DTC brand economics and specialty-retailer quarterly reorders.",
        links: [
          { label: "Small-batch handicrafts", to: "/trends/small-batch-handicrafts-reorder-cycles" },
        ],
      },
      {
        id: "india-cheaper-than-china",
        q: "Is India cheaper than China for home décor and furniture?",
        a: "Not on unit cost alone; often yes on landed cost. US MFN duty rates on most home décor, furniture and textile HTS codes are essentially identical for India-origin and China-origin. India's material US-buyer advantage is that India-origin goods sit outside the Section-301 China stack (7.5-25% additional duty on Lists 1-4 depending on HTS). Category-specific — best evaluated per-SKU against the current HTS. Read the full comparison at /trends/india-vs-china-sourcing-comparison.",
        links: [
          { label: "India vs China sourcing", to: "/trends/india-vs-china-sourcing-comparison" },
        ],
      },
      {
        id: "section-301-and-india",
        q: "What is Section 301 tariff and does India pay it?",
        a: "Section 301 is a US tariff regime applied to imports from China across four Lists (List 1-4) with 7.5-25% additional duty on top of MFN. It applies to China-origin goods only. India-origin goods are not on the Section 301 Lists, so they carry only the base MFN duty for the applicable HTS code. This is the single largest cost-side driver of US buyers shifting home-décor, furniture and textile programmes from China to India.",
        links: [],
      },
      {
        id: "us-gsp-india",
        q: "Does the US GSP programme give India duty-free treatment today?",
        a: "No. India's US GSP eligibility was terminated on 5 June 2019 by presidential proclamation. The wider US GSP programme itself lapsed on 31 December 2020 and has not been renewed by Congress since. Any Indian supplier or agent quoting you 'GSP-preferred' pricing today is misinformed. We deliberately do not price on GSP.",
        links: [],
      },
      {
        id: "payment-terms-india",
        q: "What payment terms do Indian factories accept from US buyers?",
        a: "Standard for a first programme: 30% advance TT (telegraphic transfer) at PO confirmation + 70% balance against shipping documents via DP (documents against payment) or LC (letter of credit). Larger established buyers negotiate to 20/80 or 10/90 and eventually to open account. LC through US banks (Chase, BofA, HSBC) is common on $500K+ annual programmes. Never wire 100% advance to a first-time supplier.",
        links: [
          { label: "India buying agent flow", to: "/india-buying-agent-for-usa" },
        ],
      },
    ],
  },
  {
    id: "quality-and-verification",
    label: "Quality & verification",
    intro:
      "AQL, factory audits, supplier verification and the compliance stack US buyers should demand.",
    questions: [
      {
        id: "what-is-aql-2-5",
        q: "What is AQL 2.5 inspection and why does it matter?",
        a: "AQL (Acceptable Quality Limit) 2.5 for major defects and 4.0 for minor defects is the US retail standard for consumer-goods inspection. A statistically valid random sample is drawn from a completed PO, defects are classified major or minor, and the batch is accepted or rejected against the AQL threshold per ISO 2859-1. Premium retail (design-lifestyle and premium-home buyers) tightens to AQL 1.5 major / 2.5 minor. AQL 2.5 / 4.0 is our default on every US container.",
        links: [
          { label: "AQL inspection guide", to: "/trends/aql-inspection-guide-india-sourcing" },
        ],
      },
      {
        id: "what-if-aql-fails",
        q: "What happens if the final AQL inspection fails?",
        a: "The container does not load. The factory reworks or resamples at their cost, per the QC clause written into the PO. No override without written buyer authorisation. This is a hard rule: shipping a failed AQL costs the buyer far more downstream (returns, chargebacks, retail-shelf trust) than the delay to rework.",
        links: [
          { label: "India quality control for USA", to: "/india-quality-control-for-usa" },
        ],
      },
      {
        id: "verify-indian-supplier",
        q: "How do I verify an Indian supplier before I send them money?",
        a: "Five checks minimum: (1) GST registration cross-checked with the Government of India GST portal; (2) PAN and MCA Company Master Data verified for private-limited entities; (3) IEC (Import-Export Code) status confirmed with DGFT; (4) bank reference cross-checked with the factory's declared main bank; (5) two contactable trade references contacted. All achievable in 5 working days. Never wire an advance before this file is in your inbox.",
        links: [
          { label: "India supplier verification", to: "/india-supplier-verification-for-usa" },
        ],
      },
      {
        id: "what-is-bsci",
        q: "What is BSCI and do Indian factories comply?",
        a: "BSCI (Business Social Compliance Initiative, now amfori BSCI) is a European-origin social-compliance audit scheme covering fair remuneration, working hours, health and safety, no child or forced labour, freedom of association and environmental protection. Sedex SMETA is the equivalent widely used in the UK. Roughly 60-70% of India's export-oriented factories in our vetted network hold current BSCI or Sedex certification; the remainder we can bring into scope via Intertek / SGS / Bureau Veritas.",
        links: [
          { label: "Factory audit India", to: "/services/factory-audit-india" },
        ],
      },
      {
        id: "protect-ip-india",
        q: "How do I protect my design IP when sourcing from India?",
        a: "Four layers. (1) Buyer NDA countersigned by the factory before any spec is shared; enforceable under Indian contract law. (2) No-competing-sample-display clause in the factory contract (the factory cannot show your sample to any other buyer, in any showroom, at any trade fair). (3) Tooling ownership: buyer-funded tooling is buyer-property, inventoried and photographed under a witnessed tooling agreement. (4) File design copyright in the US (and, for high-value SKUs, an Indian design registration) so infringement is actionable in both jurisdictions.",
        links: [
          { label: "India product development", to: "/india-product-development-for-usa" },
        ],
      },
    ],
  },
  {
    id: "compliance-and-freight",
    label: "US compliance & freight",
    intro:
      "The US-specific regulatory stack and the practical freight lanes India → US.",
    questions: [
      {
        id: "what-is-cpsia",
        q: "What is CPSIA and does it apply to my India-sourced goods?",
        a: "CPSIA (Consumer Product Safety Improvement Act, 2008) applies to any children's product sold in the US (including décor items marketed at children). It caps lead in accessible substrates and paint, restricts phthalates in plastics, and requires third-party testing at a CPSC-accepted laboratory (Intertek, SGS, Bureau Veritas) per 16 CFR Part 1500. Every batch requires a Children's Product Certificate. Not required for adult-only home décor, but if there is any children's-facing use case, CPSIA applies.",
        links: [
          { label: "India quality control for USA", to: "/india-quality-control-for-usa" },
        ],
      },
      {
        id: "prop-65-and-india",
        q: "Does California Prop 65 apply to India-sourced goods?",
        a: "Yes for any product sold in California. Prop 65 requires a clear and reasonable warning on products that expose Californians to any of ~900 listed chemicals above safe-harbour levels. Practically relevant for India-origin: lead in brass and paint; cadmium in glass and ceramic glazes; phthalates in soft plastics; formaldehyde in some engineered wood. We coordinate Prop 65 testing at Intertek / SGS / Bureau Veritas and, where warnings are required, draft the OEHHA-compliant warning language.",
        links: [
          { label: "Product testing compliance India", to: "/services/product-testing-compliance-india" },
        ],
      },
      {
        id: "fda-prior-notice",
        q: "How do I handle FDA prior notice for food-contact goods from India?",
        a: "Any food-contact SKU (cookware, tableware, serveware, food-storage) shipped to the US requires FDA prior notice filed at least 8 hours before arrival at the US port, plus food-contact-safe substrate certification per 21 CFR Parts 175-177. Our export documentation service files the prior notice, and we ship every food-contact SKU with SGS or Intertek migration certification (heavy-metal leach for ceramics and glass; overall migration for polymers).",
        links: [
          { label: "India quality control for USA", to: "/india-quality-control-for-usa" },
        ],
      },
      {
        id: "amazon-fba-from-india",
        q: "Can I source from India for Amazon FBA?",
        a: "Yes — a large share of our US book is FBA-first DTC brands. Amazon FBA sourcing from India requires: FNSKU labelling applied at the Indian factory or at a US-side prep centre; poly-bagging with suffocation warning where required; cartonisation to Amazon specs; and shipment plans lodged with Amazon in advance. We handle FBA-ready packaging at Indian factory (cheaper than US-side prep) and coordinate with your chosen prep service on landed complexity.",
        links: [
          { label: "India buying agent for USA", to: "/india-buying-agent-for-usa" },
        ],
      },
      {
        id: "freight-cost-india-to-usa",
        q: "How much does container freight from India to the USA cost in 2026?",
        a: "Approximate Q1 2026 rates: 40' HC container Mundra → US West Coast (LA/LB, Oakland) $2,200-2,800; Mundra → US East Coast (NY/NJ, Savannah, Norfolk) $2,800-3,400; peak-season surcharge Aug-Nov +20-30%. Transit: 24-28 days West Coast via Pacific; 30-35 days East Coast via Suez. LCL consolidation available for programmes below ~20 CBM at $170-220/CBM. Rates move weekly; treat these as an order-of-magnitude, not a quote.",
        links: [
          { label: "Container freight India 2026", to: "/trends/container-freight-india-2026" },
        ],
      },
      {
        id: "which-india-ports-us-west-coast",
        q: "Which India port ships to US West Coast?",
        a: "Mundra (Gujarat) is our default West Coast origin — fastest inland trucking from Delhi consolidation hub (18-24 hours) and direct services to LA/LB and Oakland. Nhava Sheva (Maharashtra / JNPT) is the alternative. Chennai is used for programmes originating in South India (Karur textile, Channapatna toys).",
        links: [],
      },
      {
        id: "which-india-ports-us-east-coast",
        q: "Which India port ships to US East Coast?",
        a: "Both Mundra and Nhava Sheva work for East Coast programmes. Choice depends on which shipping line has the better transit-time service for your destination (NY/NJ, Savannah, Norfolk, Charleston, Houston). Suez-routed vessels are the norm; via Cape (post-Red Sea disruption) has been an occasional alternative — check current lane conditions.",
        links: [],
      },
    ],
  },
  {
    id: "programme-and-development",
    label: "Programme structure & product development",
    intro:
      "How US buyers structure product development, tooling and reorders inside a long-term India programme.",
    questions: [
      {
        id: "how-does-product-development-work",
        q: "How does product development work with an Indian factory?",
        a: "Five stages. (1) Brief lock: CAD, reference photo or sample-in-hand + retail context + target retail price band + MOQ. (2) Factory selection for development (not every factory can develop; some can only reproduce). (3) Prototype and first article in 3-5 weeks, photographed against a colour-calibrated background and shipped to the US buyer via DHL. (4) 2-4 sample rounds inside programme fee. (5) First production batch with full AQL 2.5 QC. Read the full workflow at /india-product-development-for-usa.",
        links: [
          { label: "India product development", to: "/india-product-development-for-usa" },
        ],
      },
      {
        id: "who-owns-tooling",
        q: "Do I own tooling I've paid for at an Indian factory?",
        a: "Yes, provided you have a written tooling agreement. Buyer-funded tooling is buyer property; we inventory it (photograph it, tag it, store it at the factory under a witnessed tooling agreement). If the factory relationship ends, the tooling moves to a factory of the buyer's choice — no lock-in. Never fund tooling on a verbal understanding.",
        links: [],
      },
      {
        id: "can-i-develop-from-a-pinterest-reference",
        q: "Can I develop a product from a Pinterest reference, or do I need a CAD?",
        a: "Either works. A clear reference photograph plus a functional spec (dimensions, materials, finish, weight class, price target) is enough for our design team to hand a workable brief to a development factory. A CAD accelerates it and reduces the number of sample rounds required, but is not a prerequisite for programme kick-off.",
        links: [],
      },
      {
        id: "how-many-sample-rounds",
        q: "How many sample rounds are included in the development fee?",
        a: "3-4 rounds inside the programme fee is our default (and matches most Indian export factories with a real sample department). Rounds beyond that are quoted per iteration. Most programmes lock in 2-3 rounds. If a factory quotes zero-round or one-round development and then charges per sample, that is a warning sign — it usually means the sample department is undercapitalised.",
        links: [],
      },
      {
        id: "hospitality-ff-and-e-from-india",
        q: "Can Indian factories handle hospitality FF&E project procurement?",
        a: "Yes — we run active FF&E programmes for major international hotel operators and independent boutique-hotel developers (and their design-firm principals). Project-milestone shipping is the norm (phased despatch to construction sites), UL / FCC / lighting-compliance is bundled at factory, and CAD-driven bespoke work is comfortable at Indian small-batch MOQ. Read the workflow at /services/hotel-project-procurement-india.",
        links: [
          { label: "Hotel project procurement", to: "/services/hotel-project-procurement-india" },
        ],
      },
    ],
  },
];

export function flattenAskQuestions() {
  return ASK_TOPICS.flatMap((t) =>
    t.questions.map((q) => ({ topic: t.label, ...q }))
  );
}
